import { ViteSSG } from "vite-ssg";
import { nextTick } from "vue";

// styles

import "./assets/styles/tailwind.css";

import VueNextSelect from "vue-next-select";
import "./assets/styles/vue-next-select.css";
import { createGtag } from "vue-gtag";
import VueCookies from "vue-cookies";
import { useHead } from "@unhead/vue";
import * as Sentry from "@sentry/vue";
import posthog from "posthog-js";
import Ably from "ably";
import Toast from "vue-toastification";
import * as pkg from "vue-toastification";
import "vue-toastification/dist/index.css";

// mouting point for the whole app

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import { store, utils } from "common-frontend";
import App from "./App.vue";

// router

import routes from "./router";

const { useToast } = pkg;

const { apiPlugin, posthogPlugin } = utils;

dayjs.extend(relativeTime);
dayjs.extend(utc);

const toastOptions = {
  transition: "Vue-Toastification__slideBlurred",
  maxToasts: 20,
  newestOnTop: true,
};

// vite-ssg: `export const createApp` is required instead of the original `createApp(App).mount('#app')`
/* eslint-disable import/prefer-default-export */
export const createApp = ViteSSG(
  App,
  // vue-router options
  {
    routes,
    scrollBehavior() {
      // always scroll to top
      return { top: 0 };
    },
  },
  // function to have custom setups
  ({ app, router }) => {
    router.afterEach((to, from) => {
      /* eslint-disable */
      if (!import.meta.env.SSR && !window.doNotTrack && window._hsq) {
        window._hsq.push(["setPath", to.path]);
        _hsq.push(["trackPageView"]);
      }
      if (from.name && !import.meta.env.SSR) {
        nextTick(() => {
          posthog.capture("$pageview", {
            $current_url: import.meta.env.VITE_BASE_URL + to.fullPath
          });
        });
      }
      /* eslint-enable */
    });

    // Force a browser reload of whole page if dynamic module names have changed
    router.onError((error, to) => {
      if (!import.meta.env.SSR) {
        const reloadCount =
          parseInt(app.$cookies.get("module_reload_count"), 10) || 0;
        if (
          error.message.includes("Failed to fetch dynamically imported module")
        ) {
          if (reloadCount < 4) {
            app.$cookies.set("module_reload_count", reloadCount + 1, "5s");
            setTimeout(() => {
              window.location = to.fullPath;
            }, 3000);
          } else {
            throw error;
          }
        }
      }
    });

    app.use(router);
    app.use(store);
    app.use(apiPlugin);
    app.use(Toast, toastOptions);

    // app.config.globalProperties.$useHead = useHead;
    if (!import.meta.env.SSR) {
      if (import.meta.env.VITE_SENTRY_DSN !== "null") {
        const integrations = [
          Sentry.browserTracingIntegration({ router }),
          Sentry.replayIntegration({
            networkDetailAllowUrls: [window.location.origin],
          }),
        ];
        Sentry.init({
          app,
          dsn: import.meta.env.VITE_SENTRY_DSN,
          integrations,
          tracePropagationTargets: [import.meta.env.VITE_BASE_URL, /^\//],
          tracesSampleRate: 0.1,
          logErrors: true,
          trackComponents: true,
          replaysSessionSampleRate: 1.0,
          replaysOnErrorSampleRate: 1.0,
        });
      }
      app.use(posthogPlugin);
    }
    app.component("VueSelect", VueNextSelect);
    if (!import.meta.env.SSR && !window.doNotTrack) {
      const gtag = createGtag({
        tagId: import.meta.env.VITE_GOOGLE_ID,
        additionalAccounts: [{ id: import.meta.env.VITE_GOOGLE_ADS_ID }],
      });
      app.use(gtag);
    }

    const ably = new Ably.Realtime(import.meta.env.VITE_ABLY_KEY);

    app.config.globalProperties.$ably = ably;
    app.config.globalProperties.$dayjs = dayjs;
    app.config.globalProperties.$useHead = useHead;
    if (!import.meta.env.SSR) {
      app.config.globalProperties.$toast = useToast();
    }
    app.use(VueCookies, { expires: "7d" });
    store.$app = app;
  },
);
